import { useState, useEffect, useRef } from 'react';
import '../Solutions.css';
import { useLang } from '../context/LanguageContext';
import en from '../lang/en';
import de from '../lang/de';
import it from '../lang/it';
import fr from '../lang/fr';
import { solutionsVideo, appEngVideo, entIntVideo, digExpVideo } from '../assets/media';

const videoMap = {
  default: solutionsVideo,
  digexp: digExpVideo,
  appeng: appEngVideo,
  entint: entIntVideo,
};


function Solutions() {
  const { lang } = useLang();
  const slides = lang === 'de' ? de.solutions.slides : lang === 'it' ? it.solutions.slides : lang === 'fr' ? fr.solutions.slides : en.solutions.slides;
  const [current, setCurrent] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const [direction, setDirection] = useState('next');
  const [paused, setPaused] = useState(false);
  const pageRef = useRef(null);

  const goTo = (index) => {
    if (index === current) return;
    setDirection(index > current ? 'next' : 'prev');
    setCurrent(index);
    setAnimKey(k => k + 1);
  };

  const next = () => goTo((current + 1) % slides.length);
  const prev = () => goTo((current - 1 + slides.length) % slides.length);

  useEffect(() => {
    if (paused) return;
    const timer = setTimeout(next, 10000);
    return () => clearTimeout(timer);
  }, [current, paused]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = pageRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCurrent(0);
          setAnimKey(k => k + 1);
          setDirection('next');
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const slide = slides[current];

  return (
    <div className="sol-page" ref={pageRef} onClick={() => setPaused(p => !p)}>
      <video key={slide.video || 'default'} ref={el => { if (el) el.playbackRate = 0.5; }} className="page-bg-video" src={videoMap[slide.video] || solutionsVideo} autoPlay loop muted playsInline />
      <div className="page-bg-overlay"></div>
      <div className="sol-slider" key={animKey} data-dir={direction}>
        {paused && <div className="sol-paused-badge">⏸</div>}

        {/* Left — Image / Placeholder */}
        <div className="sol-image-side sol-img-in">
          {slide.image ? (
            <img src={slide.image} alt={slide.title} className="sol-img" />
          ) : (
            <div className="sol-img-placeholder">
              <div className="sol-placeholder-glow"></div>
              <div className="sol-quote-bg-mark">"</div>
              <div className="sol-number-badge">{slide.number}</div>
              <div className="sol-quote">
                <p className="sol-quote-text sol-qtext-in">
                  {slide.quoteBefore}{slide.quoteHighlight}{slide.quoteAfter}
                </p>
                <div className="sol-quote-divider sol-qdivider-in"></div>
                <span className="sol-quote-author sol-qauthor-in">{slide.quoteAuthor}</span>
              </div>
            </div>
          )}
        </div>

        {/* Right — Text */}
        <div className="sol-text-side">
          <span className="sol-tag sol-tag-in">{slide.tag}</span>
          <h2 className="sol-title sol-title-in">
            <span className="sol-highlight">{slide.titleBefore}{slide.titleHighlight}</span>
          </h2>
          <p className="sol-subtitle sol-subtitle-in">{slide.subtitle}</p>
          <p className="sol-body sol-body-in" style={{ borderLeftColor: slide.accent }}>{slide.body}</p>

          <ul className="sol-features sol-features-in">
            {slide.features.map((f, i) => (
              <li
                key={i}
                className="sol-feature-item"
                style={{ animationDelay: `${0.55 + i * 0.12}s` }}
              >
                <span className="sol-feature-icon">◈</span>
                {f.label}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Navigation */}
      <div className="sol-nav">
        <button className="sol-arrow" onClick={prev} aria-label="Previous">&#8592;</button>
        {slides.map((s, i) => (
          <button
            key={i}
            className={`sol-nav-label${i === current ? ' active' : ''}`}
            onClick={() => goTo(i)}
          >
            {s.titleBefore}{s.titleHighlight}
          </button>
        ))}
        <button className="sol-arrow" onClick={next} aria-label="Next">&#8594;</button>
      </div>
    </div>
  );
}

export default Solutions;
